// useActions
// Features:
// - Manages object-specific logic states (Door, Logo, Overlay, Lighting)
// - Provides declarative actions (Open, Close, Toggle)

import { create } from 'zustand'

export const DOOR_STATE = Object.freeze({
    opened: 'opened',
    closed: 'closed',
})

export const LOGO_STATE = Object.freeze({
    opened: 'opened',
    closed: 'closed',
})

export const OUTSIDE_LIGHT_STATE = Object.freeze({
    normal: 'normal',
    dimmed: 'dimmed',
})

export const OVERLAY_STATE = Object.freeze({
    opaque: 'opaque',
    transparent: 'transparent',
})

export const useActions = create((set, get) => ({
    // ======= STATES =======
    doorState: DOOR_STATE.closed,
    logoState: LOGO_STATE.closed,
    outsideLightState: OUTSIDE_LIGHT_STATE.normal,
    overlayState: OVERLAY_STATE.opaque,

    // ======= SETTERS (Internal) =======
    setDoorState: (next) => {
        const prev = get().doorState
        if (prev === next) return
        set({ doorState: next })
    },

    setLogoState: (next) => {
        const prev = get().logoState
        if (prev === next) return
        set({ logoState: next })
    },

    setOutsideLightState: (next) => {
        const prev = get().outsideLightState
        if (prev === next) return
        set({ outsideLightState: next })
    },

    setOverlayState: (next) => {
        const prev = get().overlayState
        if (prev === next) return
        set({ overlayState: next })
    },

    // ======= DOOR ACTIONS =======
    openDoor: () => {
        if (get().doorState !== DOOR_STATE.opened)
            get().setDoorState(DOOR_STATE.opened)
    },
    closeDoor: () => {
        if (get().doorState !== DOOR_STATE.closed)
            get().setDoorState(DOOR_STATE.closed)
    },
    toggleDoor: () => {
        const isOpen = get().doorState === DOOR_STATE.opened
        isOpen ? get().closeDoor() : get().openDoor()
    },

    // ======= LOGO ACTIONS =======
    showLogo: () => {
        if (get().logoState !== LOGO_STATE.opened)
            get().setLogoState(LOGO_STATE.opened)
    },
    hideLogo: () => {
        if (get().logoState !== LOGO_STATE.closed)
            get().setLogoState(LOGO_STATE.closed)
    },
    toggleLogo: () => {
        const isOpen = get().logoState === LOGO_STATE.opened
        isOpen ? get().hideLogo() : get().showLogo()
    },

    // ======= OUTSIDE LIGHT ACTIONS =======
    dimOutside: () => {
        if (get().outsideLightState !== OUTSIDE_LIGHT_STATE.dimmed)
            get().setOutsideLightState(OUTSIDE_LIGHT_STATE.dimmed)
    },
    normalizeOutside: () => {
        if (get().outsideLightState !== OUTSIDE_LIGHT_STATE.normal)
            get().setOutsideLightState(OUTSIDE_LIGHT_STATE.normal)
    },
    toggleOutsideLight: () => {
        const isDim = get().outsideLightState === OUTSIDE_LIGHT_STATE.dimmed
        isDim ? get().normalizeOutside() : get().dimOutside()
    },

    // ======= OVERLAY ACTIONS =======
    showOverlay: () => {
        if (get().overlayState !== OVERLAY_STATE.opaque)
            get().setOverlayState(OVERLAY_STATE.opaque)
    },
    hideOverlay: () => {
        if (get().overlayState !== OVERLAY_STATE.transparent)
            get().setOverlayState(OVERLAY_STATE.transparent)
    },
    toggleOverlay: () => {
        const isOpen = get().overlayState === OVERLAY_STATE.opaque
        isOpen ? get().hideOverlay() : get().showOverlay()
    },
}))
